import { useEffect, useRef, useState } from 'react';

/**
 * Animates a number from 0 to `target` over `duration` ms.
 * Uses an ease-out curve so the count slows down near the end.
 * Automatically adjusts step size so even large values finish on time.
 *
 * @param target   - The value to count up to
 * @param duration - Animation duration in milliseconds (default: 3000)
 * @param enabled  - Set to false to skip animation and show final value immediately (default: true)
 */
const useCountUp = (target: number, duration = 2500, enabled = true): number => {
    const [current, setCurrent] = useState(0);
    const frameRef = useRef<number | null>(null);
    const startTimeRef = useRef<number | null>(null);

    useEffect(() => {
        if (!enabled || target === 0) {
            setCurrent(target);
            return;
        }

        // Reset before starting a new animation
        setCurrent(0);
        startTimeRef.current = null;

        const animate = (timestamp: number) => {
            if (startTimeRef.current === null) {
                startTimeRef.current = timestamp;
            }

            const elapsed = timestamp - startTimeRef.current;
            const progress = Math.min(elapsed / duration, 1);

            // Ease-out cubic: starts fast, slows to a stop
            const eased = 1 - Math.pow(1 - progress, 3);

            setCurrent(eased * target);

            if (progress < 1) {
                frameRef.current = requestAnimationFrame(animate);
            } else {
                setCurrent(target);
            }
        };

        frameRef.current = requestAnimationFrame(animate);

        return () => {
            if (frameRef.current !== null) {
                cancelAnimationFrame(frameRef.current);
            }
        };
    }, [target, duration, enabled]);

    return current;
};

export default useCountUp;
